import {
  type AssetManifest,
  EQUIPMENT_COMPONENTS,
  type EquipmentComponent,
  type EquipmentSlotView,
  equipmentLayerAssetKey,
} from '@kenocyber/shared';
import { staticAssetUrl } from '../game/assets';
import { rarityPaint, rarityStyle } from './rarity';

/**
 * The energy system, assembled.
 *
 * ## Layers, not five squares
 *
 * The five components were rendered in ONE world under an ORTHOGRAPHIC camera
 * (`packages/asset-pipeline/src/generate-equipment-masters.ts`), so their layer
 * PNGs stack pixel-for-pixel into a single machine: the nucleus at the centre,
 * the chamber around it, the distributor ring outside that, three thick conduits
 * radiating out and the receptors they terminate in.
 *
 * Here that is five absolutely-positioned `<img>` elements filling the same
 * square, in slot order. No canvas, no WebGL, no per-part transform: the
 * alignment is baked into the art, which is the only way it can survive a
 * browser that scales the box.
 *
 * ## Mixed rarities fit
 *
 * The mounting geometry is a constant of the model file and no rarity may move
 * it, so a Prismatic core really does sit inside a Rare chamber. What changes
 * per rarity is what is drawn on the part, never where the part is.
 *
 * ## An empty slot is a hologram
 *
 * Nothing is hidden. A slot with no component draws a discreet outline of the
 * frame it belongs in, so the machine always shows what is MISSING rather than
 * quietly closing the gap - which is what makes the diagram tell a player what
 * to look for.
 *
 * ## Loaded on demand
 *
 * These are 45 files and the arena must never pay for them. Nothing here is
 * imported by the game scene; the panel that renders this component is itself
 * only mounted when the inventory opens.
 */

export interface EquipmentAssemblyProps {
  readonly slots: readonly EquipmentSlotView[];
  readonly manifest: AssetManifest;
  /** The component whose part is highlighted, or null. */
  readonly selected: EquipmentComponent | null;
  readonly onSelect: (component: EquipmentComponent) => void;
}

/**
 * The energy flow, in CSS alone.
 *
 * ## Why an overlay and not a second render
 *
 * The machine is five static PNGs and it must stay five static PNGs: a canvas
 * here would mean a second WebGL context on a screen that already has the
 * arena's, on a machine where that context is a software rasteriser. So the
 * movement is drawn by the compositor - expanding rings for the outward wave,
 * and one travelling spark per conduit.
 *
 * ## The geometry is the model's, not a guess
 *
 * The arms are rotated to the three bearings of `ASSEMBLY` and the spark
 * travels between the distributor radius and the receptor distance, both as a
 * fraction of the same 7,15 half-extent the masters were photographed in. CSS
 * rotates clockwise from +X and the model's angles run anticlockwise, so each
 * is negated - which is the whole reason these are written out rather than
 * computed inline.
 *
 * Nothing here is interactive: the overlay never takes a pointer event, so the
 * parts underneath stay clickable.
 */
const FLOW_ARMS: readonly { readonly key: string; readonly deg: number }[] = Object.freeze([
  { key: 'up', deg: -90 },
  { key: 'lower-left', deg: -210 },
  { key: 'lower-right', deg: -330 },
]);

/**
 * Where an empty slot's outline sits, as a percentage of the square.
 *
 * Measured from the same `ASSEMBLY` constants the models are built against: the
 * frame is 14.3 world units across, the core is at the origin, and the
 * receptors sit 5.62 units out along the three bearings. A hologram is the only
 * thing positioned by CSS - every filled part is positioned by its own pixels.
 */
const HOLOGRAM: Readonly<
  Record<EquipmentComponent, { top: string; left: string; size: string; label: string }>
> = Object.freeze({
  core: { top: '50%', left: '50%', size: '15%', label: 'Núcleo' },
  chamber: { top: '50%', left: '50%', size: '29%', label: 'Câmara' },
  distributor: { top: '50%', left: '50%', size: '45%', label: 'Distribuidor' },
  conduits: { top: '50%', left: '50%', size: '68%', label: 'Condutores' },
  receptor: { top: '50%', left: '50%', size: '92%', label: 'Receptores' },
});

export function EquipmentAssembly({ slots, manifest, selected, onSelect }: EquipmentAssemblyProps) {
  const bySlot = new Map(slots.map((slot) => [slot.component, slot]));
  /*
   * Nothing flows through an empty machine.
   *
   * A player with no components would otherwise watch energy circulating
   * through five holograms, which says the opposite of what the holograms are
   * there to say. The overlay is not rendered at all in that case - not hidden,
   * not paused - so an idle inventory costs the compositor nothing.
   */
  const flowing = slots.some((slot) => slot.item !== null);

  return (
    <div className="equip-assembly" data-testid="equipment-assembly">
      {EQUIPMENT_COMPONENTS.map((component) => {
        const slot = bySlot.get(component);
        const item = slot?.item ?? null;
        const hologram = HOLOGRAM[component];
        const isSelected = selected === component;

        if (!item) {
          return (
            <button
              key={component}
              type="button"
              className="equip-hologram"
              style={{
                top: hologram.top,
                left: hologram.left,
                width: hologram.size,
                height: hologram.size,
              }}
              data-testid={`equip-hologram-${component}`}
              data-selected={isSelected ? 'true' : 'false'}
              aria-label={`${slot?.label ?? component}: vazio`}
              onClick={() => onSelect(component)}
            >
              <span className="equip-hologram-label">{hologram.label}</span>
            </button>
          );
        }

        const url = staticAssetUrl(manifest, equipmentLayerAssetKey(component, item.rarity));
        return (
          <button
            key={component}
            type="button"
            className="equip-layer"
            // The rarity glow the highlight uses comes from the existing system.
            data-paint={rarityPaint(item.rarity)}
            style={rarityStyle(item.rarity)}
            data-testid={`equip-layer-${component}`}
            data-selected={isSelected ? 'true' : 'false'}
            aria-label={`${slot?.label ?? component}: ${item.componentLabel} nível ${item.level}`}
            aria-pressed={isSelected}
            onClick={() => onSelect(component)}
          >
            {url ? (
              <img src={url} alt="" draggable={false} />
            ) : (
              <span className="equip-layer-noart" aria-hidden="true" />
            )}
          </button>
        );
      })}

      {flowing ? (
        <div className="equip-flow" data-testid="equipment-flow" aria-hidden="true">
          {/*
            The outward wave: three rings leaving the nucleus on a stagger, so
            there is always one crossing the chamber and one crossing the
            distributor. The direction is the machine's own - out from the core
            to the receptors, never inward.
          */}
          <span className="equip-flow-ring" />
          <span className="equip-flow-ring" />
          <span className="equip-flow-ring" />
          {FLOW_ARMS.map((arm) => (
            <span
              key={arm.key}
              className="equip-flow-arm"
              style={{ transform: `rotate(${arm.deg}deg)` }}
            >
              <i />
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
